<template>
  <v-menu v-if="selectedItems.length" offset-y bottom right>
    <template #activator="{ props }">
      <v-btn data-testid="bulk-actions-button" class="ml-8" rounded color="primary" v-bind="props">
        {{ $t("general.actions") }}
        <v-icon icon="$triangle" />
      </v-btn>
    </template>
    <v-list>
      <v-list-item
        v-for="action in bulkActions"
        :data-testid="`bulk-action-item-${action}`"
        :key="action"
        @click="onBulkActionClick(action)"
      >
        <v-list-item-title>
          {{ $t(`manageWorkspaces.actions.${action}`) }}
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, type PropType } from "vue";
import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash/isEmpty";
import { ManageWorkspacesContext } from "@/constants/msp.ts";
import {
  defaultSubscriptionModulesConfig,
  MspWorkspaceAction,
  SubscriptionBundle,
  SubscriptionType,
  WorkspaceType,
} from "@/constants/workplaces.ts";
import { ModalWidth } from "@/constants/modals";
import { isMspActionRestricted } from "@/_helpers/msp-permissions.ts";
import { useDialogsStore } from "@/_store/dialogs.module.ts";
import UpdateBillingSubscriptionModal from "@/components/modals/subscription/UpdateSubscriptionModal.vue";
import EditMspWorkspaceLabelsModal from "@/components/modals/msp/EditMspWorkspaceLabelsModal.vue";
import ApplyPolicyToWorkspaceModal from "@/components/modals/ApplyPolicyToWorkspaceModal.vue";
import type { EditSubscriptionData, useMspStore } from "@/_store/msp/msp.module.ts";
import type { MspLabel } from "@/_store/msp/msp-labels.module.ts";
import type { ApplyGlobalPolicyData } from "@/_store/msp/global-policies/msp-global-policies.module.ts";
import { i18n } from "@/plugins/i18n";
import type { useMspTreeviewStore } from "@/_store/msp/msp-treeview.module.ts";
import {
  componentDialogsConfigConstructor,
  confirmationDialogsConfigConstructor,
} from "@/_helpers/dialog-helpers.ts";
import { MspPortalScopeSection, MspRolePermissionScopes } from "@/constants/roles.ts";

export type MspBulkActionItem = {
  workspaceId: string;
  subscriptionType: SubscriptionType;
  type: WorkspaceType;
  mspPolicyName?: string;
  displayName?: string;
  labels?: MspLabel[];
};

export type BulkActionsStore =
  ReturnType<typeof useMspStore> | ReturnType<typeof useMspTreeviewStore>;

export default defineComponent({
  props: {
    selectedItems: {
      type: Array as PropType<MspBulkActionItem[]>,
      required: true,
    },
    workspaceStore: {
      type: Object as PropType<BulkActionsStore>,
      required: true,
    },
    context: {
      type: String as PropType<ManageWorkspacesContext>,
      required: true,
    },
  },
  emits: ["on-bulk-action-applied"],
  setup(props, { emit }) {
    const dialogsStore = useDialogsStore();

    const plansWithoutSubscription = [
      SubscriptionType.INITIAL,
      SubscriptionType.FREEZE,
      SubscriptionType.TRIAL,
    ];

    const bulkActions = computed(() => {
      const showStopSubscription = props.selectedItems.some(
        (item) => !plansWithoutSubscription.includes(item.subscriptionType)
      );
      const showStartSubscription = props.selectedItems.some((item) =>
        plansWithoutSubscription.includes(item.subscriptionType)
      );
      const hasChildWorkspaces = props.selectedItems.some(
        (item) => item.type === WorkspaceType.CHILD
      );

      const actions = [MspWorkspaceAction.EDIT_SUBSCRIPTION];

      if (showStopSubscription) {
        actions.push(MspWorkspaceAction.STOP_BULK_SUBSCRIPTION);
      }

      if (showStartSubscription) {
        actions.push(MspWorkspaceAction.START_BULK_SUBSCRIPTION);
      }

      if (hasChildWorkspaces) {
        actions.push(
          MspWorkspaceAction.ADD_MSP_BULK_LABEL,
          MspWorkspaceAction.ASSIGN_GLOBAL_POLICY
        );
      }

      return actions;
    });

    const onBulkActionClick = async (action: MspWorkspaceAction) => {
      if (!props.workspaceStore) return;
      const selectedIds = props.selectedItems.map((item) => item.workspaceId);

      const openEditSubscriptionConfirmationDialog = async (payload: {
        item: EditSubscriptionData;
      }) => {
        dialogsStore.closeDialog();
        await nextTick();

        dialogsStore.openDialog(
          confirmationDialogsConfigConstructor({
            item: {},
            disable: false,
            action: MspWorkspaceAction.BULK_SUBSCRIPTION_CONFIRMATION,
            callback: async () => {
              await props.workspaceStore.editSubscription({
                ...payload.item,
                workspaceIds: selectedIds,
                modulesRemovedDuringEdit: [],
              });
              emit("on-bulk-action-applied");
            },
          })
        );
      };

      const openGlobalPolicyConfirmationDialog = async (payload: ApplyGlobalPolicyData) => {
        dialogsStore.closeDialog();
        await nextTick();

        dialogsStore.openDialog({
          ...confirmationDialogsConfigConstructor({
            action: MspWorkspaceAction.ASSIGN_GLOBAL_POLICY_CONFIRMATION,
            callback: async (dialogPayload) => {
              await props.workspaceStore.applyGlobalPolicy(dialogPayload.item);
              emit("on-bulk-action-applied");
            },
            text: i18n.global.t("modals.mspAssignGlobalPolicyConfirmation.description"),
            width: ModalWidth.SMALL,
            disable: false,
            item: payload,
          }),
          header: {
            titleClass: "text-red-dark",
            titleIcon: "warning",
            title: i18n.global.t("modals.mspAssignGlobalPolicyConfirmation.title"),
          },
        });
      };

      switch (action) {
        case MspWorkspaceAction.START_BULK_SUBSCRIPTION:
          dialogsStore.openDialog(
            confirmationDialogsConfigConstructor({
              item: {},
              action,
              callback: async (payload) => {
                await props.workspaceStore.startSubscription({
                  item: selectedIds,
                  action: payload.action,
                });
                emit("on-bulk-action-applied");
              },
              disable: isMspActionRestricted(
                MspRolePermissionScopes.MSP_PORTAL_SCOPE,
                MspPortalScopeSection.MSP_WORKSPACES,
                "startSubscription"
              ),
            })
          );
          break;
        case MspWorkspaceAction.STOP_BULK_SUBSCRIPTION:
          dialogsStore.openDialog(
            confirmationDialogsConfigConstructor({
              item: {},
              action,
              callback: async (payload) => {
                await props.workspaceStore.stopSubscription({
                  item: selectedIds,
                  action: payload.action,
                });
                emit("on-bulk-action-applied");
              },
              disable: isMspActionRestricted(
                MspRolePermissionScopes.MSP_PORTAL_SCOPE,
                MspPortalScopeSection.MSP_WORKSPACES,
                "stopSubscription"
              ),
            })
          );
          break;
        case MspWorkspaceAction.ADD_MSP_BULK_LABEL:
          dialogsStore.openDialog({
            ...componentDialogsConfigConstructor({
              action,
              callback: async (payload) => {
                await props.workspaceStore.bulkEditLabels({
                  workspaceIds: selectedIds,
                  labelIds: payload.item.labels.map((label: MspLabel) => label.id),
                });
                emit("on-bulk-action-applied");
              },
              component: EditMspWorkspaceLabelsModal,
              item: {
                labels: [],
              },
              width: ModalWidth.SMALL,
            }),
            disable: isMspActionRestricted(
              MspRolePermissionScopes.MSP_PORTAL_SCOPE,
              MspPortalScopeSection.MSP_WORKSPACES,
              "editWorkspace"
            ),
          });
          break;
        case MspWorkspaceAction.ASSIGN_GLOBAL_POLICY:
          dialogsStore.openDialog({
            ...componentDialogsConfigConstructor({
              action,
              callback: async (payload: ApplyGlobalPolicyData) => {
                if (isEmpty(payload)) return;

                const workspacesInSelection = props.selectedItems.filter((workspaceItem) => {
                  return (
                    payload.workspaces.find((ws) => ws.workspaceId === workspaceItem.workspaceId) &&
                    workspaceItem.type === WorkspaceType.CHILD
                  );
                });

                const workspacesWithPolicies = workspacesInSelection.filter(
                  (workspaceItem) => !!workspaceItem.mspPolicyName
                );

                if (workspacesWithPolicies.length > 0) {
                  await openGlobalPolicyConfirmationDialog(payload);
                } else {
                  await props.workspaceStore.applyGlobalPolicy(payload);
                  emit("on-bulk-action-applied");
                }
              },
              component: ApplyPolicyToWorkspaceModal,
              item: props.selectedItems,
              width: ModalWidth.LARGE,
            }),
            disable: isMspActionRestricted(
              MspRolePermissionScopes.MSP_PORTAL_SCOPE,
              MspPortalScopeSection.MSP_WORKSPACES,
              "editWorkspace"
            ),
          });
          break;
        case MspWorkspaceAction.EDIT_SUBSCRIPTION:
          dialogsStore.openDialog({
            ...componentDialogsConfigConstructor({
              action,
              callback: async (payload) => {
                await openEditSubscriptionConfirmationDialog(payload);
              },
              component: UpdateBillingSubscriptionModal,
              item: {
                bundles: [] as SubscriptionBundle[],
                modules: cloneDeep(defaultSubscriptionModulesConfig),
              },
              width: ModalWidth.LARGE,
              disable: isMspActionRestricted(
                MspRolePermissionScopes.MSP_PORTAL_SCOPE,
                MspPortalScopeSection.MSP_WORKSPACES,
                "editSubscription"
              ),
            }),
            scrollable: true,
            fullscreen: true,
          });
          break;
      }
    };

    return {
      bulkActions,
      onBulkActionClick,
    };
  },
});
</script>
